/**
 * Что лежит в домашней аптечке.
 *
 * Аптечка отвечает на три вопроса, и все три — глазами, без расчётов: что у
 * меня есть от этого, годно ли оно ещё и хватит ли. Поэтому отбор здесь
 * простой, а вся арифметика срока живёт в `entities/medication/model/expiry`:
 * таблетница и аптечка обязаны понимать «годен до» одинаково.
 */

import { expiryState } from '@/entities/medication/model/expiry';
import { formatDose } from '@/entities/medication/model/medication-meta';
import { isMix } from '@/entities/medication/model/mix';
import { medicationSearchFields } from '@/entities/medication/model/search';
import type { Medication } from '@/shared/db/schema';
import { type IsoDate, today } from '@/shared/lib/datetime';
import { distinctLabels, hasWordStartingWith, matchesSearch } from '@/shared/lib/search';

/**
 * Что действительно лежит дома.
 *
 * Архив — это то, чего дома уже нет: закончившийся курс убирают, просроченное
 * выбрасывают. Показывать архив в аптечке значило бы предлагать принять то,
 * чего в ящике не найдётся.
 */
export function selectCabinet(medications: readonly Medication[]): Medication[] {
  return medications.filter(
    (medication) =>
      medication.deletedAt === null &&
      medication.archivedAt === null &&
      // Курс, для которого лекарства смешивают, в ящике не лежит: в нём лежат
      // его составляющие, у каждой свой срок годности и свой остаток.
      !isMix(medication),
  );
}

/**
 * Лекарства аптечки, на которые можно завести курс, — по тому, что набрано в
 * названии.
 *
 * Курс на лекарство из ящика заводился второй записью, и в аптечке появлялся
 * двойник. Поэтому, пока человек печатает название курса, ему показывают, что
 * такое уже лежит дома, — по началу слов, как ищется всё остальное: «нур»
 * находит «Нурофен». Только то, что просто лежит: идущий курс — уже
 * применение, второй курс на ту же упаковку заводят осознанно.
 */
export function takeableFromCabinet(items: readonly Medication[], name: string): Medication[] {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.join('').length < 2) return [];

  return items.filter(
    (item) =>
      item.courseType === 'none' &&
      !isMix(item) &&
      words.every((word) => hasWordStartingWith([item.name], word)),
  );
}

export interface ExpiringMedications {
  /** Срок вышел: принимать нельзя. */
  expired: Medication[];
  /** Срок на исходе: месяц или меньше. */
  soon: Medication[];
}

/**
 * Лекарства, о сроке которых стоит сказать вслух.
 *
 * Внутри каждой группы — по возрастанию даты: первым то, что кончилось раньше
 * всех или кончится раньше всех. Лекарства без указанного срока сюда не
 * попадают: приложение не знает о них ничего и молчит, вместо того чтобы
 * пугать или успокаивать наугад.
 */
export function selectExpiring(
  medications: readonly Medication[],
  reference: IsoDate = today(),
): ExpiringMedications {
  const expired: { medication: Medication; date: IsoDate }[] = [];
  const soon: { medication: Medication; date: IsoDate }[] = [];

  for (const medication of medications) {
    const state = expiryState(medication, reference);
    if (state.date === null) continue;
    if (state.status === 'expired') expired.push({ medication, date: state.date });
    if (state.status === 'soon') soon.push({ medication, date: state.date });
  }

  const byDate = (a: { date: IsoDate }, b: { date: IsoDate }) => (a.date < b.date ? -1 : 1);

  return {
    expired: expired.sort(byDate).map((entry) => entry.medication),
    soon: soon.sort(byDate).map((entry) => entry.medication),
  };
}

/**
 * Поиск по аптечке — по всей карточке лекарства (`medicationSearchFields`),
 * теми же правилами, что и везде (`matchesSearch`). Пока поиск не смотрел в
 * заметку, «мигрень» не находила «Нурофен» с пометкой «от мигрени помогает
 * лучше».
 */
export function searchCabinet(medications: readonly Medication[], query: string): Medication[] {
  return medications.filter((medication) => matchesSearch(medicationSearchFields(medication), query));
}

/**
 * Остаток словами: «12 таблеток», «60 мл».
 *
 * `null` — остаток не ведут, и выдумывать нечего: «0» в этом случае означал бы
 * «кончилось», хотя на деле человек просто не считает таблетки.
 */
export function describeStock(medication: Medication): string | null {
  if (medication.stockQuantity === null) return null;

  return formatDose(medication.stockQuantity, medication.doseUnit, medication.form);
}

/**
 * Места хранения, которые человек завёл.
 *
 * Аптечка дома одна не всегда: коробка в ванной, сумка, бардачок в машине,
 * холодильник. Вопрос «что у меня есть» у каждой из них свой — в дороге
 * содержимое домашней полки бесполезно.
 *
 * Список собирается из самих записей: отдельного справочника мест нет
 * намеренно — он завёлся бы пустым и требовал заполнения до того, как человек
 * записал первое лекарство.
 */
export function listStoragePlaces(medications: readonly Medication[]): string[] {
  // «Машина» и «машина» — одно место, остаётся первое написание.
  return distinctLabels(medications.map((medication) => medication.storagePlace));
}

/**
 * Строка о том, что в аптечке требует внимания.
 *
 * `null` — говорить не о чем, и тогда строки нет вовсе: «всё в порядке» на
 * экране, куда заходят по несколько раз в день, быстро перестают читать, а
 * вместе с ней перестают читать и предупреждения.
 */
export function describeCabinetAlert({ expired, soon }: ExpiringMedications): string | null {
  const parts: string[] = [];

  if (expired.length > 0) parts.push(`просрочено ${expired.length}`);
  if (soon.length > 0) parts.push(`у ${soon.length} кончается срок`);
  if (parts.length === 0) return null;

  return `В аптечке: ${parts.join(' · ')}`;
}
